import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { Seo } from "@/components/Seo";
import { buildVideoJsonLd } from "@/data/installVideos";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import StickyCta from "@/components/StickyCta";
import HomePage from "@/pages/HomePage";
import ProductsPage from "@/pages/ProductsPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import ComparePage from "@/pages/ComparePage";
import CompareFramesPage from "@/pages/CompareFramesPage";
import CorporatePage from "@/pages/CorporatePage";
import InstitutionsPage from "@/pages/InstitutionsPage";
import DealerPortalPage from "@/pages/DealerPortalPage";
import DealerLocatorPage from "@/pages/DealerLocatorPage";
import InstallationPage from "@/pages/InstallationPage";
import FaqPage from "@/pages/FaqPage";
import TPostGuidePage from "@/pages/TPostGuidePage";
import BenefitsPage from "@/pages/BenefitsPage";
import NewsPage from "@/pages/NewsPage";
import NewsPostPage from "@/pages/NewsPostPage";
import ContactPage from "@/pages/ContactPage";
import AboutPage from "@/pages/AboutPage";
import QualityPage from "@/pages/QualityPage";
import DealerSetupPage from "@/pages/DealerSetupPage";
import DealerProfilePage from "@/pages/DealerProfilePage";
import AdminDealersPage from "@/pages/AdminDealersPage";
import NotFoundPage from "@/pages/NotFoundPage";
import DealerAssetsPage from "@/pages/DealerAssetsPage";

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, easing: (t) => 1 - Math.pow(1 - t, 3) });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const ROUTE_META = {
  "/": { t: "The Banner Bar | Wind-Resistant Outdoor Banner Frame Kits", d: "Patented wind-resistant outdoor banner frame kits for the sign industry. Wholesale dealer pricing, franchise rollouts. Made in USA. Patent 10,410,553." },
  "/products": { t: "Kits & Hardware | The Banner Bar", d: "Four wind-resistant banner frame kits plus replacement bungees, covers and hardware. T-post mounted, customer-installable, wholesale pricing for sign shops." },
  "/compare": { t: "Compare All Kits | The Banner Bar", d: "Compare Small Form, Two Post, Three Post and V-Config banner frame kits side by side: banner sizes, posts, bars, wind rating and ideal use." },
  "/frame-comparison": { t: "Banner Frame Comparison | The Banner Bar", d: "How The Banner Bar beats rope, wood and angle-iron banner framing: wind-resistant design, reusable hardware and fast customer installs." },
  "/corporate": { t: "Corporate & Franchise Rollouts | The Banner Bar", d: "Standardize outdoor banners across every franchise location with one wind-resistant frame standard, a national dealer network and volume quotes." },
  "/institutions": { t: "Cities, Schools & Institutions | The Banner Bar", d: "Wind-resistant, reusable banner frame kits for cities, schools and institutions — event, athletics and municipal messaging that outlasts the campaign." },
  "/dealer-portal": { t: "Wholesale Dealer Portal | The Banner Bar", d: "Wholesale portal for Banner Bar dealers: dealer pricing, online ordering, install videos and the dealer asset library. Apply or log in." },
  "/dealer-setup": { t: "Activate Your Dealer Account | The Banner Bar", d: "", noindex: true },
  "/dealer/profile": { t: "Confirm Your Details | The Banner Bar", d: "", noindex: true },
  "/admin/dealers": { t: "Dealer Activation Tracker | The Banner Bar", d: "", noindex: true },
  "/dealer-assets": { t: "Dealer Assets | The Banner Bar", d: "Dealer asset library for Banner Bar retailers and sign shops: product photography, logos, sell sheets and install media." },
  "/dealer-locator": { t: "Find Your Local Sign Shop | The Banner Bar", d: "Find a sign shop near you that sells and installs The Banner Bar wind-resistant banner frame kits. Search the map by state or city." },
  "/installation": { t: "Installation Videos | The Banner Bar", d: "Step-by-step Banner Bar install videos for the Small Form, Two Post, Three Post and V-Config kits — a typical install takes about 15 minutes." },
  "/faqs": { t: "FAQs | The Banner Bar", d: "Answers to common Banner Bar questions: banner sizing, installation, T-post specs, wind resistance, bungees, wholesale pricing and the dealer program." },
  "/tpost-guide": { t: "Where to Buy T-Posts | The Banner Bar", d: "Where to buy T-posts for your Banner Bar kit: the 6 ft 6 in TP125 or TP133, 1.25 lb/ft spec and tips for sourcing them locally." },
  "/benefits": { t: "Benefits | The Banner Bar", d: "Why The Banner Bar: a patented wind-resistant frame, replaceable bungees, reusable hardware, fast installs and Made in USA build quality." },
  "/news": { t: "News | The Banner Bar", d: "Install techniques, dealer resources and product updates from The Banner Bar shop floor at Outdoor Display Products in Montrose, Colorado." },
  "/contact": { t: "Contact | The Banner Bar", d: "Talk to a human in Colorado. Questions on sizing, wholesale or a franchise rollout — call 970-650-5173, email, or send the contact form." },
  "/about": { t: "About | The Banner Bar", d: "Outdoor Display Products builds The Banner Bar in Montrose, Colorado — the patented, wind-resistant banner frame system, made in USA." },
  "/quality-guarantee": { t: "Quality Guarantee | The Banner Bar", d: "The Banner Bar quality guarantee: American-made frames, UV-protected components and replaceable wear parts, backed by the people who build them." },
};

function MetaSync() {
  const { pathname } = useLocation();
  const meta = ROUTE_META[pathname];
  const isDynamic = pathname.startsWith("/products/") || pathname.startsWith("/news/");
  const fallbackT = pathname.startsWith("/products/") ? "Kit Details | The Banner Bar"
    : pathname.startsWith("/news/") ? "News | The Banner Bar"
    : "The Banner Bar | Wind-Resistant Outdoor Banner Frame Kits";
  // Dynamic routes (product detail, news post) render their own <Seo> once data loads, which
  // overrides these tags; MetaSync provides the static-route meta and the fallback meanwhile.
  return <Seo title={meta ? meta.t : fallbackT} description={meta ? meta.d : ""} path={pathname} noindex={meta?.noindex || isDynamic === false && !meta} jsonLd={pathname === "/installation" ? buildVideoJsonLd() : undefined} />;
}

function AnalyticsSync() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (window.gtag) window.gtag("event", "page_view", { page_path: pathname + search, page_title: document.title });
  }, [pathname, search]);
  return null;
}

const LEGACY_REDIRECTS = {
  "/products.html": "/products",
  "/compare-frames.html": "/compare",
  "/banner-bar-small-form.html": "/products/small-form-kit",
  "/banner-bar-two-post-kit.html": "/products/two-post-kit",
  "/three-post-kit.html": "/products/three-post-kit",
  "/banner-bar-v-config-kit.html": "/products/v-config-kit",
  "/bungees.html": "/products/replacement-bungees",
  "/black-pole-covers.html": "/products",
  "/banner-bar-benefits.html": "/benefits",
  "/installation.html": "/installation",
  "/videos.html": "/installation",
  "/faqs.html": "/faqs",
  "/t-posts.html": "/tpost-guide",
  "/news.html": "/news",
  "/contact.html": "/contact",
  "/about.html": "/about",
  "/mission-statement.html": "/about",
  "/quality-guarantee.html": "/quality-guarantee",
  "/bannerbardealer.html": "/dealer-locator",
  "/resources.html": "/installation",
  "/outdoor-banner-frame-pricing.html": "/dealer-portal",
  "/dealer-assets-405896.html": "/dealer-assets",
  "/fastsigns.html": "/dealer-portal",
  "/find-a-dealer": "/dealer-locator",
  "/dealers": "/dealer-locator",
  "/independent.html": "/dealer-portal",
  "/no-banners-on-the-ground.html": "/benefits",
  "/grommet-template.html": "/installation",
  "/privacy-policy.html": "/",
  "/terms-of-use.html": "/",
};

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <SmoothScroll />
          <MetaSync />
          <AnalyticsSync />
          <ScrollToTop />
          <div className="min-h-screen bg-obsidian pb-14 text-slate-100 lg:pb-0">
            <Navbar />
            <main>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/products/:slug" element={<ProductDetailPage />} />
                <Route path="/compare" element={<ComparePage />} />
                <Route path="/frame-comparison" element={<CompareFramesPage />} />
                <Route path="/corporate" element={<CorporatePage />} />
                <Route path="/institutions" element={<InstitutionsPage />} />
                <Route path="/dealer-portal" element={<DealerPortalPage />} />
                <Route path="/dealer-setup" element={<DealerSetupPage />} />
                <Route path="/dealer/profile" element={<DealerProfilePage />} />
                <Route path="/admin/dealers" element={<AdminDealersPage />} />
                <Route path="/dealer-assets" element={<DealerAssetsPage />} />
                <Route path="/dealer-locator" element={<DealerLocatorPage />} />
                <Route path="/installation" element={<InstallationPage />} />
                <Route path="/faqs" element={<FaqPage />} />
                <Route path="/tpost-guide" element={<TPostGuidePage />} />
                <Route path="/benefits" element={<BenefitsPage />} />
                <Route path="/news" element={<NewsPage />} />
                <Route path="/news/:slug" element={<NewsPostPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/quality-guarantee" element={<QualityPage />} />
                {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
                  <Route key={from} path={from} element={<Navigate to={to} replace />} />
                ))}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </main>
            <Footer />
            <CartDrawer />
            <StickyCta />
            <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#111827", border: "1px solid #334155", color: "#F8FAFC" } }} />
          </div>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
